import React, { useState, useEffect } from "react";
import "./PublicForm.css";
import bymoLogo from "../../img/bymo.png";
import { FaSpinner } from 'react-icons/fa';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? 'http://127.0.0.1:8080'
    : 'https://bymolive-backend-995926394015.us-central1.run.app';

// Generate a random password with 2 letters and 2 numbers
const generateRandomPassword = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    
    let password = '';
    
    // Add 2 random letters
    for (let i = 0; i < 3; i++) {
        password += letters.charAt(Math.floor(Math.random() * letters.length));
    }
    
    // Add 2 random numbers
    for (let i = 0; i < 3; i++) {
        password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    }
    
    return password;
};

// Reimplemented functions that were in PublicFormActions.js
const validateDiscountCode = async (code) => {
    try {
        const response = await fetch(
            `${API_BASE_URL}/api/onboarding/validate-discount?code=${encodeURIComponent(code.toUpperCase())}`
        );
        return response.ok;
    } catch (error) {
        console.error("Error validating discount code:", error);
        return false;
    }
};

const processOnboarding = async (data) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/onboarding/process`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Error processing onboarding");
        }

        return await response.json();
    } catch (error) {
        console.error("Error in processOnboarding:", error);
        throw error;
    }
};

const fetchPrograms = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/programs`);
        if (!response.ok) {
            throw new Error("Nu am putut încărca programele");
        }
        const data = await response.json();
        
        // Process programs to parse rules if they're stored as strings
        const processedPrograms = (data.programs || []).map(program => {
            if (program.rules && typeof program.rules === 'string') {
                try {
                    program.rules = JSON.parse(program.rules);
                } catch (e) {
                    console.error("Error parsing program rules:", e);
                    program.rules = { benefits: [], requirements: [], note: '' };
                }
            }
            return program;
        });
        
        return processedPrograms;
    } catch (error) {
        console.error("Error fetching programs:", error);
        throw error;
    }
};

const PublicFormValidated = ({ profileData }) => {
    console.log("PublicFormValidated received profileData:", profileData);
    
    const [message, setMessage] = useState("");
    const [isSubmissionComplete, setIsSubmissionComplete] = useState(false);
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        address: "",
        code: "",
    });
    const [isAgreementChecked, setIsAgreementChecked] = useState(false);
    const [codeValidationMessage, setCodeValidationMessage] = useState("");
    const [programInfo, setProgramInfo] = useState(null);
    const [initialBonus, setInitialBonus] = useState(profileData.initialBonus || "");
    const [commissionPercentage, setCommissionPercentage] = useState(profileData.commission || "");
    const [isLoadingProgram, setIsLoadingProgram] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [buttonText, setButtonText] = useState("Trimite!");
    const [isProcessing, setIsProcessing] = useState(false);
    const [isCodeValid, setIsCodeValid] = useState(false);
    const [existingDiscountCodes, setExistingDiscountCodes] = useState([]);
    const [isLoadingCodes, setIsLoadingCodes] = useState(true);
    const [isButtonLoading, setIsButtonLoading] = useState(false);

    // Load discount codes when component mounts
    useEffect(() => {
        const fetchDiscountCodes = async () => {
            try {
                setIsLoadingCodes(true);
                console.log("Fetching discount codes from API...");
                const response = await fetch(`${API_BASE_URL}/api/onboarding/discount-codes`);
                
                console.log("Discount codes API response status:", response.status);
                
                const data = await response.json();
                console.log("Discount codes API response data:", data);
                
                if (data.discount_codes) {
                    console.log(`Fetched ${data.discount_codes.length} discount codes`);
                    setExistingDiscountCodes(data.discount_codes);
                } else {
                    console.warn("No discount_codes array in response");
                    setExistingDiscountCodes([]);
                }
                
                // If there's debug info, log it
                if (data.debug_info) {
                    console.warn("Discount codes API debug info:", data.debug_info);
                }
            } catch (error) {
                console.error("Error fetching discount codes:", error);
                // If we can't fetch codes, we'll validate against an empty array
                setExistingDiscountCodes([]);
            } finally {
                setIsLoadingCodes(false);
            }
        };
        
        fetchDiscountCodes();
    }, []);

    // Load program information when component mounts
    useEffect(() => {
        console.log("Loading program info for program ID:", profileData.program);
        
        const loadProgramInfo = async () => {
            if (profileData.program) {
                setIsLoadingProgram(true);
                try {
                    const programs = await fetchPrograms();
                    console.log("Fetched programs:", programs);
                    
                    const program = programs.find(p => p.id === profileData.program);
                    console.log("Found matching program:", program);
                    
                    if (program) {
                        setProgramInfo(program);
                        
                        // Update initial bonus and commission from program data
                        // Handle both camelCase and snake_case property names
                        if (program.initial_bonus || program.initialBonus) {
                            const bonus = program.initial_bonus || program.initialBonus;
                            console.log("Setting initial bonus from program:", bonus);
                            setInitialBonus(bonus);
                        }
                        
                        if (program.commission) {
                            console.log("Setting commission from program:", program.commission);
                            setCommissionPercentage(program.commission);
                        }
                    } else {
                        console.warn("Program not found:", profileData.program);
                    }
                } catch (error) {
                    console.error("Error fetching program details:", error);
                } finally {
                    setIsLoadingProgram(false);
                }
            }
        };

        loadProgramInfo();
    }, [profileData.program]);

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        
        // Convert email to lowercase and code to uppercase
        let processedValue = value;
        if (name === "email") {
            processedValue = value.toLowerCase();
        } else if (name === "code") {
            processedValue = value.toUpperCase();
        }
        
        setFormData(prev => ({ ...prev, [name]: processedValue }));

        // Validate discount code as user types (if it's the code field)
        if (name === "code" && processedValue.trim()) {
            const upperCaseCode = processedValue.toUpperCase();
            const codeExists = existingDiscountCodes.includes(upperCaseCode);
            
            setCodeValidationMessage(
                codeExists 
                    ? "✗ Acest cod exista deja." 
                    : "✓ Codul ales este disponibil."
            );
            setIsCodeValid(!codeExists);
        }
    };

    const handleSubmit = async () => {
        if (!formData.fullName || !formData.email || !formData.phone || !formData.address || !formData.code) {
            setMessage("Te rugăm să completezi toate câmpurile obligatorii.");
            return;
        }

        if (!isCodeValid) {
            setMessage("Te rugăm să alegi un cod valid.");
            return;
        }

        if (!isAgreementChecked) {
            setMessage("Te rugăm să bifezi că ești de acord cu termenii.");
            return;
        }

        // Start loading state
        setIsButtonDisabled(true);
        setIsButtonLoading(true);
        setMessage("Procesăm înregistrarea ta...");
        
        // Generate a random password
        const password = generateRandomPassword();
        console.log("Generated password:", password);
        
        // Prepare user data object that will be used in multiple steps
        let userDataForDB = {
            fullName: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            code: formData.code,
            password: password,
            program: profileData.program,
            profileName: profileData.profileName,
            initialBonus: initialBonus,
            commission: commissionPercentage
        };
        
        try {
            // Step 1: Create customer in Shopify
            console.log("Step 1: Creating customer in Shopify");
            let customerData;
            try {
                const createCustomerResponse = await fetch(`${API_BASE_URL}/api/onboarding/create-customer`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: formData.email,
                        first_name: formData.fullName.split(' ')[0],
                        last_name: formData.fullName.split(' ').slice(1).join(' ') || formData.fullName.split(' ')[0]
                    }),
                });
                
                customerData = await createCustomerResponse.json();
                
                if (createCustomerResponse.ok) {
                    console.log("Customer created successfully:", customerData);
                    
                    // Add the Shopify customer ID to our user data
                    if (customerData.customer_id) {
                        userDataForDB.shopifyCustomerId = customerData.customer_id;
                    }
                } else {
                    console.error("Error creating customer:", customerData.error);
                    if (customerData.error && customerData.error.includes("already exists")) {
                        console.log("Customer already exists, retrieving customer ID");
                    }
                }
            } catch (error) {
                console.error("Error in create customer step:", error);
            }
            
            // Step 2: If we couldn't create customer, try to get customer ID
            let customerId = customerData?.customer_id;
            if (!customerId) {
                console.log("Step 2: Getting customer ID");
                try {
                    const response = await fetch(`${API_BASE_URL}/api/onboarding/get-customer-id?email=${encodeURIComponent(formData.email)}`);
                    if (response.ok) {
                        const data = await response.json();
                        customerId = data.customer_id;
                        console.log("Retrieved customer ID:", customerId);
                        
                        // Add the retrieved Shopify customer ID to our user data
                        userDataForDB.shopifyCustomerId = customerId;
                    } else {
                        console.log("Could not retrieve customer ID");
                    }
                } catch (error) {
                    console.error("Error getting customer ID:", error);
                }
            }
            
            // Step 2.5: Create user in Firebase Authentication
            console.log("Step 2.5: Creating user in Firebase Auth");
            try {
                const createAuthResponse = await fetch(`${API_BASE_URL}/api/onboarding/create-auth-user`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: formData.email,
                        password: password,
                        display_name: formData.fullName,
                        custom_claims: {
                            role: "customer",
                            shopifyCustomerId: customerId,
                            code: formData.code
                        }
                    }),
                });
                
                if (createAuthResponse.ok) {
                    const authData = await createAuthResponse.json();
                    console.log("Firebase Auth user created/updated successfully:", authData);
                    
                    // Store the Firebase UID for later use
                    if (authData.firebase_uid) {
                        userDataForDB.firebaseUid = authData.firebase_uid;
                    }
                } else {
                    const errorData = await createAuthResponse.json();
                    console.error("Error creating Firebase Auth user:", errorData.error);
                }
            } catch (error) {
                console.error("Error in Firebase Auth user creation step:", error);
            }
            
            // Step 3: Add store credit if we have a customer ID and initial bonus
            console.log("Step 3: Adding store credit");
            if (customerId && initialBonus && parseFloat(initialBonus) > 0) {
                try {
                    const storeCredit = parseFloat(initialBonus);
                    console.log(`Adding ${storeCredit} store credit for customer ${customerId}`);
                    
                    const addCreditResponse = await fetch(`${API_BASE_URL}/api/onboarding/add-store-credit`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            customer_id: customerId,
                            amount: storeCredit,
                            note: `Initial bonus for ${profileData.profileName} - ${formData.code}`
                        }),
                    });
                    
                    console.log("Store credit API response status:", addCreditResponse.status);
                    
                    const creditData = await addCreditResponse.json();
                    
                    if (addCreditResponse.ok) {
                        console.log("Store credit added successfully:", creditData);
                    } else {
                        console.error("Error adding store credit:", creditData.error || "Unknown error");
                    }
                } catch (error) {
                    console.error("Error in store credit API call:", error);
                }
            } else {
                console.log("Skipping store credit step - no customer ID or initial bonus");
            }
            
            // Step 4: Create user in database
            console.log("Step 4: Creating user in database");
            try {
                const createUserResponse = await fetch(`${API_BASE_URL}/api/onboarding/create-user-db`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        user_id: formData.email,
                        user_data: userDataForDB
                    }),
                });
                
                if (createUserResponse.ok) {
                    const userData = await createUserResponse.json();
                    console.log("User created in database successfully:", userData);
                } else {
                    const errorData = await createUserResponse.json();
                    console.error("Error creating user in database:", errorData.error);
                }
            } catch (error) {
                console.error("Error creating user in database:", error);
            }
            
            // Step 5: Create discount code in Shopify
            console.log("Step 5: Creating discount code in Shopify");
            try {
                const createDiscountResponse = await fetch(`${API_BASE_URL}/api/onboarding/create-discount`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        discount_code: formData.code.toUpperCase()
                    }),
                });
                
                if (createDiscountResponse.ok) {
                    const discountData = await createDiscountResponse.json();
                    console.log("Discount code created successfully:", discountData);
                } else {
                    const errorData = await createDiscountResponse.json();
                    console.warn("Error creating discount code:", errorData.error);
                }
            } catch (error) {
                console.error("Error creating discount code:", error);
            }
            
            // Final step: Send welcome email
            console.log("Final step: Sending welcome email");
            try {
                const sendEmailResponse = await fetch(`${API_BASE_URL}/api/onboarding/send-welcome`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: formData.email,
                        password: password,
                        code: formData.code,
                        initialBonus: initialBonus,
                        commission: commissionPercentage,
                        programRules: programInfo ? programInfo.rules : null
                    }),
                });
                
                if (sendEmailResponse.ok) {
                    console.log("Welcome email sent successfully");
                } else {
                    console.warn("Error sending welcome email");
                }
            } catch (error) {
                console.error("Error sending welcome email:", error);
            }
            
            // Show success message
            setMessage("Felicitări! Ai fost înregistrat cu succes în programul BYMO Athletes. Vei primi un email cu detaliile contului tău.");
            setIsSubmissionComplete(true);
            
        } catch (error) {
            console.error("Error in onboarding process:", error);
            setMessage(`A apărut o eroare: ${error.message}`);
        } finally {
            // Stop loading
            setIsButtonLoading(false);
            setIsButtonDisabled(false);
        }
    };

    const getProgramRules = () => {
        if (!programInfo) {
            return null;
        }
        
        try {
            const rulesObj = typeof programInfo.rules === 'string' 
                ? JSON.parse(programInfo.rules) 
                : programInfo.rules || {};
            
            return {
                benefits: rulesObj.benefits || [],
                requirements: rulesObj.requirements || [],
                note: rulesObj.note || ''
            };
        } catch (error) {
            console.error("Error parsing program rules:", error);
            return null;
        }
    };

    const rules = getProgramRules();

    return (
        <div className="form-container">
            {isProcessing || isSubmissionComplete ? (
                <div className="confirmation-box-big-onboarding">
                    <img src={bymoLogo} alt="BYMO Logo" className="logo-user-login" />
                    <div className="confirmation-box">
                        {message || "În procesare..."} 
                    </div>
                </div>
            ) : (
                <>
                    <div className="form-header">
                        <img src={bymoLogo} alt="BYMO Logo" className="logo" />
                        <h2>Onboarding</h2>
                    </div>
                    
                    <div className="form-content">
                        <h3>Profil Instagram: @{profileData.profileName}</h3>
                        
                        <div className="form-group">
                            <label>Nume Complet:</label>
                            <input
                                type="text"
                                name="fullName"
                                placeholder="Nume și Prenume"
                                value={formData.fullName}
                                onChange={handleFieldChange}
                                className="input-field"
                            />
                        </div>
                        <div className="form-group">
                            <label>Email:</label>
                            <input
                                type="email"
                                name="email"
                                placeholder="email@example.com"
                                value={formData.email}
                                onChange={handleFieldChange}
                                className="input-field"
                            />
                        </div>
                        <div className="form-group">
                            <label>Nr. Telefon:</label>
                            <input
                                type="text"
                                name="phone"
                                placeholder="0741 234 567"
                                value={formData.phone}
                                onChange={handleFieldChange}
                                className="input-field"
                            />
                        </div>
                        <div className="form-group">
                            <label>Oras si Adresa:</label>
                            <input
                                type="text"
                                name="address"
                                placeholder="Adresa unde poti primi produse BYMO"
                                value={formData.address}
                                onChange={handleFieldChange}
                                className="input-field"
                            />
                        </div>
                        <div className="form-group">
                            <label>Codul tau de reducere:</label>
                            <input
                                type="text"
                                name="code"
                                placeholder="ex: ANA10"
                                value={formData.code}
                                onChange={handleFieldChange}
                                className="input-field"
                            />
                            <span
                                style={{
                                    color: codeValidationMessage === "✓ Codul ales este disponibil." ? "green" : "red",
                                    marginLeft: "10px",
                                }}
                            >
                                {codeValidationMessage}
                            </span>
                        </div>

                        {isLoadingProgram ? (
                            <div className="loading-section">
                                <p>Se încarcă informațiile programului...</p>
                            </div>
                        ) : (
                            <div className="information-section">
                                {programInfo ? (
                                    <>
                                        <h3>Beneficiaza de {initialBonus || "0"} lei pe BYMO.ro</h3>
                                        <h3 style={{ color: "#dedede", marginTop: "20px", marginBottom: "20px" }}>――――――――― </h3>
                                        <h3>Beneficii ca ambasador:</h3>
                                        <ul>
                                            {rules && rules.benefits && rules.benefits.length > 0 ? (
                                                rules.benefits.map((benefit, index) => (
                                                    <li key={index}>{benefit}</li>
                                                ))
                                            ) : (
                                                <li>Contactează-ne pentru detalii despre beneficii</li>
                                            )}
                                        </ul>
                                        <h3 style={{ color: "#dedede", marginTop: "20px", marginBottom: "20px" }}>――――――――― </h3>
                                        <h3>Comision {commissionPercentage || "0"}% pentru fiecare vânzare</h3>
                                        <h3 style={{ color: "#dedede", marginTop: "20px", marginBottom: "20px" }}>――――――――― </h3>
                                        <h3>Cerinte minime:</h3>
                                        <ul>
                                            {rules && rules.requirements && rules.requirements.length > 0 ? (
                                                rules.requirements.map((requirement, index) => (
                                                    <li key={index}>{requirement}</li>
                                                ))
                                            ) : (
                                                <li>Contactează-ne pentru detalii despre cerințe</li>
                                            )}
                                        </ul>
                                        {rules && rules.note && (
                                            <p style={{ color: "#757575", marginTop: "30px", marginBottom: "30px" }}>
                                                {rules.note}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <h3>Informații program</h3>
                                        <p>Te rugăm să ne contactezi pentru mai multe informații.</p>
                                        <p style={{ color: "#757575", marginTop: "20px" }}>
                                            Trimite un email la <a href="mailto:hello@bymo.ro">hello@bymo.ro</a> sau
                                            contactează-ne pe Instagram <a href="https://instagram.com/bymo.ro" target="_blank" rel="noopener noreferrer">@bymo.ro</a>
                                        </p>
                                    </>
                                )}
                                <div className="agreement-section">
                                    <input
                                        type="checkbox"
                                        id="agreement"
                                        checked={isAgreementChecked}
                                        onChange={(e) => setIsAgreementChecked(e.target.checked)}
                                    />
                                    <label htmlFor="agreement">Am inteles si sunt de acord</label>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={handleSubmit}
                            disabled={isButtonDisabled || isButtonLoading}
                            className="action-button"
                        >
                            {isButtonLoading ? (
                                <>
                                    <FaSpinner className="spinner" /> Se procesează...
                                </>
                            ) : (
                                buttonText
                            )}
                        </button>
                        
                        {message && (
                            <div className="message info-message">
                                {message}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default PublicFormValidated; 